import { FC, useMemo } from "preact/compat";
import { useSignal, useSignalEffect } from "@preact/signals";
import { useApp } from "~/core/store";
import { BaseClassicChoice, ClassicSelect } from "~/components";
import ContentEditorFooter from "../ContentEditorFooter";
import { TabProps } from "./interfaces";
import PencilIcon from "@material-design-icons/svg/outlined/create.svg";
import SettingsIcon from "@material-design-icons/svg/outlined/settings.svg";
import Loader from "~/assets/loader.svg";

const TagSummary: FC<TabProps> = ({ tag, locale }) => {
    const {
        api: { discovery },
        snack,
    } = useApp();
    const selectors = useMemo(
        (): BaseClassicChoice[] => [
            { id: "$", name: "Fallback summary" },
            ...(tag.selectors ?? []).map(
                (v): BaseClassicChoice => ({
                    id: v.selector,
                    name: v.name,
                }),
            ),
        ],
        [tag],
    );
    const selector = useSignal<BaseClassicChoice | null>(selectors[0]);
    const info = discovery.getByTag(tag.tag);
    const loading = discovery.loading(tag.tag);
    const content = useSignal("");
    const saving = useSignal(false);

    useSignalEffect(() => {
        if (!selector.value?.id) {
            return;
        }

        content.value =
            info.value?.summary?.[selector.value.id] ??
            info.value?.summary?.$ ??
            "";
    });

    const handleSave = async () => {
        if (!selector.value) {
            return;
        }

        saving.value = true;

        try {
            await discovery.setTagSummary({
                tag: tag.tag,
                selector: selector.value.id,
                summary: content.value,
                lang: locale,
            });
        } catch (err: unknown) {
            const message =
                err instanceof Error ? err.message : "An error occurred";
            snack.error("Error occurred", message);
        } finally {
            saving.value = false;
        }
    };

    const existingContent =
        (selector.value?.id
            ? info.value?.summary?.[selector.value.id]
            : info.value?.summary?.$) ?? "";
    const dirty = content.value !== existingContent;

    return (
        <>
            <div className="overflow-y-overlay flex h-full flex-col space-y-6 p-4">
                <div className="flex w-full flex-col">
                    <h3 className="mb-2 flex items-center text-gray-500">
                        <SettingsIcon
                            width="24"
                            height="24"
                            className="mr-2 fill-current"
                        />
                        <span>Selector</span>
                    </h3>
                    <div className="space-y-2">
                        <ClassicSelect
                            className="mr-4 max-w-[180px]"
                            choices={selectors}
                            value={selector.value}
                            onChange={(value) => (selector.value = value)}
                            emptyText="Selector"
                            by="id"
                        />
                        {selector.value && (
                            <p>
                                <span>Description: </span>
                                <span>{selector.value?.name}</span>
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex h-full w-full flex-col">
                    <h3 className="mb-2 flex items-center text-gray-500">
                        {loading.value ? (
                            <Loader width="24" height="24" className="mr-2" />
                        ) : (
                            <PencilIcon
                                width="24"
                                height="24"
                                className="mr-2 fill-current"
                            />
                        )}
                        <span>Content</span>
                    </h3>
                    <textarea
                        value={content}
                        onInput={({ currentTarget }) =>
                            (content.value = currentTarget.value)
                        }
                        className="h-full w-full resize-none rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 focus:ring-offset-white"
                        placeholder="Enter content here..."
                    />
                </div>
            </div>
            <ContentEditorFooter
                disabled={saving.value || !dirty}
                loading={saving.value}
                onClick={handleSave}
            />
        </>
    );
};

export default TagSummary;
